import type { HevcFallbackReason } from './hevcDecodeGating';
import type { PlayerPerfStats } from './types';
import type Hls from 'hls.js';
import type { ErrorData, HlsConfig, Level } from 'hls.js';

import { useStaleGuard } from '~/utils';

import {
	bufferedAhead,
	canHardwareDecodeHevc,
	codecLockIndices,
	framesDecodedSince,
	hevcDecodeFailedThisSession,
	hevcDecodingInfo,
	hevcFallbackIndices,
	hevcLevelsToRemove,
	isDecodeStall,
	isHevcLevel,
	isVideoCodecTypeSupported,
	markHevcDecodeFailed,
	oversizedLevelIndices,
	pickStartLevel,
	relaxOverstatedHevcLevels,
	resolveLevelIndices,
	sizeCapIndex
} from './hevcDecodeGating';
import { fetchHlsResource } from './shakaLoader';
import { elementShortSide, startTarget } from './startTarget';

/**
 * hls.js atrás do MESMO contrato que o app consumia do Shaka (attach/load/
 * configure/unload/destroy, buffering/adaptation/error, variant tracks).
 * O QoE, o shared player e o useHlsPlayer não precisam conhecer o motor.
 * Rede passa por fetchHlsResource — o pipeline com cache/priority/bps que o
 * scheme plugin do Shaka aplicava.
 */

interface EngineConfig {
	streaming?: {
		bufferingGoal?: number;
		bufferBehind?: number;
	};
	abr?: {
		enabled?: boolean;
		defaultBandwidthEstimate?: number;
		restrictions?: { maxBandwidth?: number };
	};
}

export interface EngineVariantTrack {
	id: number;
	height: number | null;
	width: number | null;
	bandwidth: number;
	videoCodec?: string;
	active: boolean;
}

type EngineEvent = 'buffering' | 'adaptation' | 'error';

const DEFAULT_LOAD_TIMEOUT_MS = 30_000;
const MAX_RECOVERY_ATTEMPTS = 2;
// Over twice the Android HEVC startup p90 (1.3s, Sep/2026): past this with data
// buffered and no frame, the decoder is not coming back.
const DECODE_STALL_MS = 3_000;
// Quality ramp entries kept per playback: the start and the first switches are
// what tells a slow climb apart; past that the telemetry field only grows.
const MAX_RAMP_ENTRIES = 12;

// hls.js stretches the forward buffer to maxBufferSize / bitrate (60 MB by
// default) and only maxMaxBufferLength (600 s) caps it, so a "3 s" preload
// pulled the whole short. A preload stops at its first segments; the active
// player keeps up to 30 s or 8 MB ahead.
const PRELOAD_BUFFER_S = 3;
const ACTIVE_MAX_BUFFER_S = 30;
const ACTIVE_MAX_BUFFER_BYTES = 8_000_000;

export function bufferConfig(
	isPreload: boolean,
	goal: number
): Partial<HlsConfig> {
	if (isPreload) {
		return {
			maxBufferLength: PRELOAD_BUFFER_S,
			maxMaxBufferLength: PRELOAD_BUFFER_S
		};
	}
	return {
		maxBufferLength: goal,
		maxMaxBufferLength: Math.max(goal, ACTIVE_MAX_BUFFER_S),
		maxBufferSize: ACTIVE_MAX_BUFFER_BYTES
	};
}

// Its own action, not just a field on video_qoe: abandoned plays never reach the
// QoE snapshot, and they are the ones the fallback exists for.
function reportHevcFallback(context: Record<string, unknown>): void {
	try {
		window.DD_RUM?.addAction('hevc_fallback', context);
	} catch {
		// Telemetry must never break playback.
	}
}

let HlsCtor: typeof Hls | null = null;
let hlsPromise: Promise<typeof Hls> | null = null;

export function loadHlsEngine(): Promise<typeof Hls> {
	if (!import.meta.client) {
		return Promise.reject(new Error('hls.js can only be loaded on the client'));
	}
	if (!hlsPromise) {
		hlsPromise = import('hls.js').then(m => {
			HlsCtor = m.default;
			return m.default;
		});
	}
	return hlsPromise;
}

export function isEngineSupported(): boolean {
	return Boolean(HlsCtor?.isSupported());
}

function buildLoader(
	Ctor: typeof Hls,
	onBytes: (bytes: number) => void,
	isPreload: () => boolean
): HlsConfig['loader'] {
	// O hls.js nao diz "este e o primeiro fragmento", e so ele destrava o
	// primeiro frame: um contador por instancia de player marca os primeiros.
	// So o player que nao e preload ganha o boost — um vizinho fora de tela
	// nao deve competir com o player que o usuario esta assistindo.
	let mediaRequestCount = 0;

	return class PipelineLoader {
		context: unknown = null;
		stats = {
			aborted: false,
			loaded: 0,
			retry: 0,
			total: 0,
			chunkCount: 0,
			bwEstimate: 0,
			loading: { start: 0, first: 0, end: 0 },
			parsing: { start: 0, end: 0 },
			buffering: { start: 0, first: 0, end: 0 }
		};
		private abortController = new AbortController();
		// O hls.js destroi o loader logo apos o sucesso; sem esta guarda todo
		// fragmento concluido terminaria marcado como abortado e o ABR
		// descartaria a amostra de banda.
		private settled = false;
		private timeoutTimer: ReturnType<typeof setTimeout> | null = null;

		private clearTimer(): void {
			if (this.timeoutTimer !== null) {
				clearTimeout(this.timeoutTimer);
				this.timeoutTimer = null;
			}
		}

		load(context: any, config: any, callbacks: any): void {
			this.context = context;
			this.stats.loading.start = performance.now();
			// Contrato do hls.js: config e o LoaderConfiguration achatado (mesmo
			// shape de loadPolicy.default), maxLoadTimeMs no topo — nao aninhado.
			const timeoutMs = config?.maxLoadTimeMs ?? DEFAULT_LOAD_TIMEOUT_MS;
			this.timeoutTimer = setTimeout(() => {
				if (this.settled) return;
				this.settled = true;
				this.timeoutTimer = null;
				this.stats.aborted = true;
				this.abortController.abort();
				callbacks.onTimeout(this.stats, context, null);
			}, timeoutMs);
			// Manifests chegam como texto; fragmentos/init como arraybuffer.
			const isMediaRequest = context.responseType === 'arraybuffer';
			// Faixa de audio separada nao deve consumir o boost do fragmento de video.
			const isAudioRequest = context.frag?.type === 'audio';
			// Em CMAF o primeiro request e o init, entao o fragmento pesado seria o segundo.
			const isPriorityMediaRequest =
				!isPreload() &&
				isMediaRequest &&
				!isAudioRequest &&
				++mediaRequestCount <= 2;
			void fetchHlsResource(context.url, {
				signal: this.abortController.signal,
				...(isPriorityMediaRequest ? { priority: 'high' as const } : {}),
				// hls.js reads these stats live (frag.stats is this object): the
				// abandon rules see a slow fragment while it loads, and TTFB and
				// bandwidth are measured from the first byte, not the last.
				onProgress: (loaded, total) => {
					if (this.settled) return;
					this.stats.loading.first ||= Math.max(
						performance.now(),
						this.stats.loading.start
					);
					this.stats.loaded = loaded;
					this.stats.total = total;
				}
			})
				.then(({ uri, data, status }) => {
					if (this.settled) return;
					this.settled = true;
					this.clearTimer();
					const now = performance.now();
					// Cache and primed manifests never reach the network.
					this.stats.loading.first = this.stats.loading.first || now;
					this.stats.loading.end = now;
					this.stats.loaded = this.stats.total = data.byteLength;
					onBytes(data.byteLength);
					let payload: ArrayBuffer | string = data;
					if (context.responseType !== 'arraybuffer') {
						payload = new TextDecoder().decode(data);
						if (context.type === 'manifest') {
							payload = relaxOverstatedHevcLevels(
								payload,
								isVideoCodecTypeSupported
							);
						}
					}
					callbacks.onSuccess(
						{ url: uri, data: payload, code: status },
						this.stats,
						context,
						null
					);
				})
				.catch((err: Error & { status?: number }) => {
					if (this.settled || err?.name === 'AbortError') return;
					this.settled = true;
					this.clearTimer();
					callbacks.onError(
						{ code: err.status ?? 0, text: err.message },
						context,
						null,
						this.stats
					);
				});
		}

		abort(): void {
			this.clearTimer();
			if (this.settled) return;
			this.settled = true;
			this.stats.aborted = true;
			this.abortController.abort();
		}

		destroy(): void {
			this.abort();
			this.context = null;
		}
	} as unknown as HlsConfig['loader'];
}

/**
 * Highest level index the ABR may use, given a bandwidth budget.
 *
 * `autoLevelCapping` is an index CEILING, and the ladder is not monotonic in
 * bitrate: hls.js sorts levels by height, so the H.264 fallback rung can sit
 * between two HEVC ones. One of our published masters reads 669k, 1401k, 1055k,
 * 2225k.
 *
 * The obvious reading is that taking the last index that fits admits a more
 * expensive level below the ceiling. It does not, and stopping at the first
 * level over budget is actively harmful:
 *
 *   - the ceiling is not what enforces the budget. Every candidate inside it
 *     still has to pass `adjustedbw >= bitrate` in findBestLevel, and the loop
 *     runs from the highest index down, so a cheaper rung above the expensive
 *     one is always reached first.
 *   - the ceiling decides the CODEC FAMILY. getCodecTiers only sees
 *     `levels.slice(minAutoLevel, maxAutoLevel + 1)`, so truncating at the
 *     first expensive rung can leave the H.264 rung as the only tier. The ABR
 *     never crosses codec families afterwards, and the session would be stuck
 *     outside HEVC for good.
 *   - measured on the ladder above, truncating early costs the 720p rung at
 *     1055k for measured bandwidth between 704 and 934 kbps, a 37% drop with
 *     no gain.
 */
export function levelCapForBandwidth(
	levels: ReadonlyArray<{ bitrate: number }>,
	maxBandwidth: number
): number {
	// -1 is "no cap" in hls.js, which is the honest answer for an empty ladder.
	if (!levels.length) return -1;
	// A budget that is not a finite number is not a budget. Returning the whole
	// ladder here would turn a missing value into "no limit at all".
	if (!Number.isFinite(maxBandwidth)) return 0;

	let cap = 0;
	for (let i = 0; i < levels.length; i++) {
		const bitrate = levels[i]!.bitrate;
		// A level without a usable bitrate is not counted as fitting.
		if (Number.isFinite(bitrate) && bitrate <= maxBandwidth) cap = i;
	}
	return cap;
}

const RESIZE_DEBOUNCE_MS = 150;

export class EnginePlayer {
	constructor(private isPreload = false) {}

	private hls: Hls | null = null;
	private media: HTMLMediaElement | null = null;
	private listeners: Record<EngineEvent, Set<(event: any) => void>> = {
		buffering: new Set(),
		adaptation: new Set(),
		error: new Set()
	};

	private pendingConfig: EngineConfig = {};
	private abrEnabled = true;
	private maxBandwidth: number | null = null;
	private mediaListeners: Array<[string, () => void]> = [];
	// Rotation or a window resize moves the size cap; debounced, since resize
	// fires many times a second while dragging.
	private resizeTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly onWindowResize = (): void => {
		if (this.resizeTimer) clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(() => {
			this.resizeTimer = null;
			this.applyBandwidthCap();
		}, RESIZE_DEBOUNCE_MS);
	};
	private loadingPaused = false;
	// MANIFEST_PARSED arrived and prepareStart() still owes the startLoad().
	private startPending = false;
	// Where the HEVC fallback's reload starts. With autoStartLoad off hls.js
	// ignores config.startPosition, and the reload clears lastCurrentTime.
	private resumeAt: number | null = null;

	// Bytes que o player realmente puxou. Cruzado com o quanto foi assistido,
	// diz quanto da banda virou desperdicio.
	private bytesFetched = 0;

	getBytesFetched(): number {
		return this.bytesFetched;
	}

	// Startup phases and quality ramp of the current playback (QoE telemetry).
	private loadStartedAt = 0;
	private manifestParsedAt: number | null = null;
	private firstFragAt: number | null = null;
	private qualityRamp: string[] = [];
	private levelSwitches = 0;

	getPerfStats(): PlayerPerfStats {
		const estimate = this.hls?.bandwidthEstimate;
		return {
			url: this.sourceUrl,
			bytesFetched: this.bytesFetched,
			manifestParsedAt: this.manifestParsedAt,
			firstFragAt: this.firstFragAt,
			levelSwitches: this.levelSwitches,
			qualityRamp: this.qualityRamp.join(','),
			bandwidthEstimate:
				estimate && Number.isFinite(estimate) ? Math.round(estimate) : null
		};
	}

	/** "ms since load:height"; the first entry is the start rung, not a switch. */
	private recordLevelSwitch(index: number): void {
		const height = this.hls?.levels[index]?.height;
		if (!height) return;
		if (this.qualityRamp.length > 0) this.levelSwitches++;
		if (this.qualityRamp.length < MAX_RAMP_ENTRIES) {
			const at = Math.round(performance.now() - this.loadStartedAt);
			this.qualityRamp.push(`${at}:${height}`);
		}
	}
	// destroy() invalida: uma continuacao de attach()/load() de geracao velha
	// perdeu a corrida e nao pode anexar um Hls orfao ao elemento.
	private readonly staleGuard = useStaleGuard();
	private networkRecoveries = 0;
	private mediaRecoveries = 0;
	// Generation the HEVC gating already ran for. Keyed to the generation, not
	// a boolean: every load() advances it, so there is no reset to forget and a
	// load() that fails before its manifest cannot wedge the next one.
	private hevcGatedGeneration = -1;
	// Whether the device can hardware-decode HEVC, determined before Hls construction
	// per hls.js v1.7.0 API.md: videoPreference belongs in constructor config for
	// initial level selection, not post-parse mutation.
	// https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
	private hevcHardwareOk = false;
	// Whether the gating removed HEVC rungs for the current video (telemetry).
	private hevcLevelsRemoved = false;
	// Why this video dropped HEVC after the device failed to decode it, or null.
	private hevcFallbackReason: HevcFallbackReason | null = null;
	// The ladder had H.264 rungs before the codec lock removed them: the HEVC
	// fallback can reload into them.
	private ladderHasH264 = false;
	// What MediaCapabilities answered, kept apart from the session memory for telemetry.
	private hevcHardwareClaimed = false;
	private decodeStallTimer: ReturnType<typeof setTimeout> | null = null;
	// Once this video shows a frame, a stall is no longer a decode failure.
	private decodeConfirmed = false;
	private framesAtLoad = 0;
	private sourceUrl: string | null = null;
	// Le pelo listener de ERROR permanente (attach), consumido pelo listener
	// temporario de load(): os dois disparam na mesma emissao sincrona do
	// hls.js, entao esta flag conta se a recuperacao ja foi tentada.
	private lastRecoveryAttempted = false;

	configure(config: EngineConfig): void {
		this.pendingConfig = {
			streaming: { ...this.pendingConfig.streaming, ...config.streaming },
			abr: { ...this.pendingConfig.abr, ...config.abr }
		};
		if (config.abr) {
			if (config.abr.enabled !== undefined) {
				this.abrEnabled = config.abr.enabled;
			}
			if (config.abr.restrictions !== undefined) {
				this.maxBandwidth = config.abr.restrictions.maxBandwidth ?? null;
			}
			if (this.hls) {
				// Voltar para "auto" precisa soltar o nivel pinado: no hls.js e o
				// currentLevel (-1) que reativa o ABR de fato, nao a flag local.
				if (config.abr.enabled === true) {
					this.hls.currentLevel = -1;
				}
				if (config.abr.defaultBandwidthEstimate) {
					this.hls.bandwidthEstimate = config.abr.defaultBandwidthEstimate;
				}
				this.applyBandwidthCap();
			}
		}
	}

	getConfiguration(): { abr: { enabled: boolean } } {
		return { abr: { enabled: this.abrEnabled } };
	}

	private applyBandwidthCap(): void {
		if (!this.hls) return;
		const levels = this.hls.levels;
		const sizeCap = sizeCapIndex(levels, this.startTarget());
		if (this.maxBandwidth === null) {
			this.hls.autoLevelCapping = sizeCap;
			return;
		}
		// No ladder yet (before MANIFEST_PARSED): nothing to cap. The right
		// value is applied as soon as the levels exist.
		if (levels.length === 0) return;
		const bandwidthCap = levelCapForBandwidth(levels, this.maxBandwidth);
		this.hls.autoLevelCapping =
			sizeCap === -1 ? bandwidthCap : Math.min(bandwidthCap, sizeCap);
	}

	/** The element this player draws into, as the warm chain sees it. */
	private startTarget() {
		return startTarget(elementShortSide(this.media as HTMLElement | null));
	}

	/**
	 * Pin the first fragment before startLoad(), on the rung pickStartLevel
	 * chooses (the warm chain downloads the same one).
	 * Must stay synchronous — hls.js resolves the start level on startLoad,
	 * which used to race an async MANIFEST_PARSED pin when startLevel was -1.
	 */
	private pinStartLevelSync(): void {
		if (!this.hls) return;
		const levels = this.hls.levels;
		if (!levels?.length) {
			this.hls.startLevel = 0;
			return;
		}
		const start = pickStartLevel(
			levels,
			this.hevcHardwareOk,
			this.startTarget()
		);
		this.hls.startLevel = start;
		this.hls.nextLoadLevel = start;
	}

	/**
	 * Drops HEVC rungs the device can only software-decode (desktop Chromium
	 * without a hardware decoder) using hls.js documented removeLevel API, so
	 * the ABR stays on H.264 instead of stuttering on every CMAF segment.
	 * Runs once per manifest, before startLoad(); keeps the ladder untouched when
	 * MediaCapabilities is missing or reports smooth hardware decode.
	 *
	 * This is the documented removeLevel use case per hls.js v1.7.0 API.md:
	 * "removeLevel is for unsupported/error-prone renditions after parse."
	 * https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#hlsremovelevelindex
	 */
	private async gateHevcDecode(): Promise<void> {
		if (!this.hls) return;
		// Captured without claiming a generation: load() owns it. One pass per
		// generation; and a video switch mid-await swaps the ladder, so a stale
		// generation drops the result below instead of stripping H.264 rungs.
		const generation = this.staleGuard.current();
		if (this.hevcGatedGeneration === generation) return;
		this.hevcGatedGeneration = generation;
		const decodingInfo = hevcDecodingInfo();
		const snapshot = this.hls.levels;
		const toRemove = await hevcLevelsToRemove(snapshot, decodingInfo);
		if (this.staleGuard.isStale(generation) || !this.hls) return;

		if (toRemove.length > 0) {
			// The generation only tracks video switches. Within the same load hls.js
			// can still drop a rung on its own (codec or key error), so a snapshot
			// index may now name another rung. Re-resolve by identity against the live
			// ladder; the helper skips rungs already gone and never returns them all.
			const indices = resolveLevelIndices(snapshot, toRemove, this.hls.levels);
			if (indices.length > 0) {
				// High-to-low so each index is still valid as the ladder shrinks.
				for (const index of indices.sort((a, b) => b - a)) {
					this.hls.removeLevel(index);
				}
				this.hevcLevelsRemoved = true;
			}
		}
	}

	/**
	 * Settles the ladder, then starts loading. A rung removed after startLoad()
	 * had already cost its playlist and often its first segment, and the pin
	 * could land on an HEVC rung the gate was about to drop. Whatever happens
	 * on the way, the load still starts: a throw would leave a black card.
	 */
	private async prepareStart(): Promise<void> {
		if (!this.hls) return;
		const generation = this.staleGuard.current();
		this.startPending = true;
		try {
			await this.settleLadder(generation);
		} finally {
			if (!this.staleGuard.isStale(generation) && this.hls) {
				this.startPending = false;
				if (!this.loadingPaused) this.startLoadOnce();
			}
		}
	}

	private async settleLadder(generation: number): Promise<void> {
		if (!this.hls) return;
		// The first fragment must not be a rung this device cannot take. Removed,
		// not capped: hls.js capLevelToPlayerSize rewrites autoLevelCapping and
		// would drop the bandwidth cap.
		this.removeLevels(oversizedLevelIndices(this.hls.levels));
		// After the HEVC fallback's reload, the rungs that leave are the fallback's,
		// not the gating's (telemetry tells the two apart).
		const gating = !this.hevcFallbackReason;
		if (
			hevcDecodeFailedThisSession() &&
			this.removeLevels(hevcFallbackIndices(this.hls.levels)) &&
			gating
		) {
			this.hevcLevelsRemoved = true;
		}
		await this.gateHevcDecode();
		if (this.staleGuard.isStale(generation) || !this.hls) return;
		// One codec per playback; after an HEVC failure, H.264.
		this.ladderHasH264 = this.hls.levels.some(level => !isHevcLevel(level));
		const lockToHevc = this.hevcHardwareOk && gating;
		const outside = codecLockIndices(this.hls.levels, lockToHevc);
		if (
			this.removeLevels(outside) &&
			gating &&
			!this.hls.levels.some(level => isHevcLevel(level))
		) {
			this.hevcLevelsRemoved = true;
		}
		this.applyBandwidthCap();
		// Pin before startLoad so the first fragment is HEVC-lowest (or 0).
		this.pinStartLevelSync();
	}

	/** The first startLoad of a load; only that one honors resumeAt. */
	private startLoadOnce(): void {
		const position = this.resumeAt ?? -1;
		this.resumeAt = null;
		this.hls?.startLoad(position);
	}

	/** Whether HEVC rungs were removed from the current ladder. */
	wasHevcGated(): boolean {
		return this.hevcLevelsRemoved;
	}

	/** Whether this video fell back to H.264 after a real HEVC decode failure. */
	wasHevcFallback(): boolean {
		return this.hevcFallbackReason !== null;
	}

	private activeLevel(): Level | undefined {
		if (!this.hls) return undefined;
		const index =
			this.hls.currentLevel >= 0 ? this.hls.currentLevel : this.hls.loadLevel;
		return this.hls.levels[index];
	}

	private isLoadingHevc(): boolean {
		const level = this.activeLevel();
		return !!level && isHevcLevel(level);
	}

	/** Removes the given rungs, high to low so each index stays valid. */
	private removeLevels(indices: number[]): boolean {
		if (!this.hls || indices.length === 0) return false;
		for (const index of [...indices].sort((a, b) => b - a)) {
			this.hls.removeLevel(index);
		}
		return true;
	}

	private totalVideoFrames(): number | null {
		const media = this.media;
		if (!(media instanceof HTMLVideoElement)) return null;
		return media.getVideoPlaybackQuality?.().totalVideoFrames ?? null;
	}

	/** The <video> element reported MEDIA_ERR_DECODE; see fallBackFromHevc. */
	recoverFromDecodeFailure(): boolean {
		// The element and hls.js can both report one failure; whichever comes second
		// belongs to the fallback already under way.
		if (this.hevcFallbackReason) return this.retryAfterFallback();
		return this.fallBackFromHevc('element_decode');
	}

	/**
	 * The codec switch can throw one more media error. Still the same recovery, so
	 * it is retried within the usual budget instead of shown; past it, it surfaces.
	 */
	private retryAfterFallback(): boolean {
		if (!this.hls || this.mediaRecoveries >= MAX_RECOVERY_ATTEMPTS)
			return false;
		this.mediaRecoveries++;
		this.hls.recoverMediaError();
		return true;
	}

	/**
	 * The device claimed hardware HEVC and could not decode it. Retrying the same
	 * rung fails the same way, so the rest of this video goes to H.264. The codec
	 * lock took the H.264 rungs out of the ladder, so the source is reloaded
	 * locked to them from where playback stopped; loadSource with the media
	 * detached re-creates the MediaSource, so no HEVC data sits in front of the
	 * new codec. Returns false when there is nothing to fall back to, so the
	 * caller keeps its usual error handling.
	 */
	private fallBackFromHevc(
		reason: HevcFallbackReason,
		detail?: string
	): boolean {
		const media = this.media;
		if (
			!this.hls ||
			!media ||
			!this.sourceUrl ||
			!this.ladderHasH264 ||
			!this.isLoadingHevc()
		)
			return false;
		const failed = this.activeLevel();
		this.clearDecodeStallTimer();
		markHevcDecodeFailed(reason, detail);
		this.hevcFallbackReason = reason;
		// Capture the attempt before recovery or a subsequent load resets its state.
		media.dispatchEvent(
			new CustomEvent('hevcfallback', {
				detail: { url: this.sourceUrl }
			})
		);
		// prepareStart() runs again on MANIFEST_PARSED and, with the fallback
		// reason set, locks this playback to H.264 and starts at resumeAt.
		this.resumeAt = media.currentTime > 0 ? media.currentTime : null;
		const wasPlaying = !media.paused;
		this.hls.detachMedia();
		this.hls.loadSource(this.sourceUrl);
		this.hls.attachMedia(media);
		if (wasPlaying) this.playOnceReady(media);
		reportHevcFallback({
			reason,
			detail: detail ?? null,
			codec: failed?.videoCodec ?? null,
			width: failed?.width ?? null,
			height: failed?.height ?? null,
			bitrate: failed?.bitrate ?? null,
			hardware_claimed: this.hevcHardwareClaimed,
			preload: this.isPreload
		});
		return true;
	}

	/**
	 * Detach/attach runs the element's load algorithm, which pauses it without a
	 * pause event; the reel player does not call play() on canplay.
	 */
	private playOnceReady(media: HTMLMediaElement): void {
		const generation = this.staleGuard.current();
		media.addEventListener(
			'canplay',
			() => {
				if (this.staleGuard.isStale(generation) || this.media !== media) return;
				media.play().catch(() => {});
			},
			{ once: true }
		);
	}

	private clearDecodeStallTimer(): void {
		if (this.decodeStallTimer) clearTimeout(this.decodeStallTimer);
		this.decodeStallTimer = null;
	}

	private armDecodeStallCheck(): void {
		const media = this.media;
		if (
			!(media instanceof HTMLVideoElement) ||
			media.paused ||
			this.loadingPaused ||
			// Network wait is not decode time: start only once playable data arrived.
			bufferedAhead(media.buffered, media.currentTime) < 1 ||
			this.decodeStallTimer ||
			this.decodeConfirmed ||
			this.hevcFallbackReason ||
			!this.isLoadingHevc()
		)
			return;
		const generation = this.staleGuard.current();
		this.decodeStallTimer = setTimeout(() => {
			this.decodeStallTimer = null;
			const media = this.media;
			if (!(media instanceof HTMLVideoElement)) return;
			if (this.staleGuard.isStale(generation)) return;
			// A paused element (a preload reels ahead) is not decoding yet; 'play' re-arms.
			if (media.paused || this.loadingPaused) return;
			const decodedFrames = framesDecodedSince(
				this.totalVideoFrames(),
				this.framesAtLoad
			);
			if (decodedFrames) {
				this.decodeConfirmed = true;
				return;
			}
			const stalled = isDecodeStall({
				readyState: media.readyState,
				bufferedAhead: bufferedAhead(media.buffered, media.currentTime),
				decodedFrames
			});
			if (stalled) this.fallBackFromHevc('stall');
		}, DECODE_STALL_MS);
	}

	async attach(media: HTMLMediaElement): Promise<void> {
		const generation = this.staleGuard.next();
		const Ctor = await loadHlsEngine();
		if (this.staleGuard.isStale(generation)) return;

		// Pre-check HEVC hardware decode capability before Hls construction.
		// hls.js v1.7.0 API.md: videoPreference limits initial level selection
		// when set in constructor config. Must be determined before loadSource.
		// https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
		// Memoized per session by hevcDecodingInfo().
		this.hevcHardwareClaimed = await canHardwareDecodeHevc(hevcDecodingInfo());
		// A decode failure earlier in the session outranks what MediaCapabilities claims.
		this.hevcHardwareOk =
			this.hevcHardwareClaimed && !hevcDecodeFailedThisSession();
		if (this.staleGuard.isStale(generation)) return;

		this.media = media;
		this.loadingPaused = false;
		const streaming = this.pendingConfig.streaming ?? {};
		const abr = this.pendingConfig.abr ?? {};
		this.hls = new Ctor({
			loader: buildLoader(
				Ctor,
				bytes => {
					this.bytesFetched += bytes;
				},
				() => this.isPreload
			),
			...bufferConfig(this.isPreload, streaming.bufferingGoal ?? 10),
			backBufferLength: streaming.bufferBehind ?? 30,
			abrEwmaDefaultEstimate: abr.defaultBandwidthEstimate ?? 2_000_000,
			// Hold startLoad until we pin synchronously on MANIFEST_PARSED.
			// startLevel: -1 + async pin raced hls.js (it starts on MANIFEST_LOADED).
			// Default 0 = cheapest; pinStartLevelSync upgrades to lowest HEVC when HW ok.
			// https://github.com/video-dev/hls.js/blob/v1.7.3/docs/API.md#autostartload
			autoStartLoad: false,
			startLevel: 0,
			enableWorker: true,
			testBandwidth: false,
			// hls.js v1.7.0 API.md: videoPreference limits initial level selection
			// to a codec family (if a baseline variant ≤1080p 30fps exists). Codec
			// switching mid-play generally requires manual level change. Setting
			// videoCodec to 'hvc1' (fourCC family) when HW decode is confirmed guides
			// initial selection toward HEVC instead of lingering on AVC ~640p (Safari/iOS).
			// https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
			...(this.hevcHardwareOk
				? { videoPreference: { videoCodec: 'hvc1' } }
				: {})
		});
		if (this.staleGuard.isStale(generation)) {
			this.hls.destroy();
			this.hls = null;
			this.media = null;
			return;
		}
		this.hls.on(
			Ctor.Events.LEVEL_SWITCHED,
			(_event: unknown, data: { level: number }) => {
				this.recordLevelSwitch(data?.level);
				for (const cb of this.listeners.adaptation) cb({});
			}
		);
		this.hls.on(Ctor.Events.MANIFEST_PARSED, () => {
			// The first parse: a fallback reload parses again mid-playback.
			this.manifestParsedAt ??= performance.now();
			void this.prepareStart();
		});
		// removeLevel (erro de codec, MediaCapabilities, erro de chave) encurta a
		// escada e desloca os indices sem tocar no autoLevelCapping, entao o teto
		// passaria a apontar para um degrau mais caro do que o escolhido.
		this.hls.on(Ctor.Events.LEVELS_UPDATED, () => {
			this.applyBandwidthCap();
		});
		this.hls.on(
			Ctor.Events.FRAG_BUFFERED,
			(_event: unknown, data: { frag?: { type?: string } }) => {
				if (data?.frag?.type === 'main') {
					this.firstFragAt ??= performance.now();
				}
				this.armDecodeStallCheck();
			}
		);
		this.hls.on(Ctor.Events.ERROR, (_event: unknown, data: ErrorData) => {
			if (!data.fatal) return;
			// Not surfaced: the reel would draw its error screen over a playback that
			// is about to continue on H.264.
			if (data.type === Ctor.ErrorTypes.MEDIA_ERROR) {
				const recovering = this.hevcFallbackReason
					? this.retryAfterFallback()
					: this.fallBackFromHevc('media_error', data.details);
				if (recovering) {
					this.lastRecoveryAttempted = true;
					return;
				}
			}
			const message =
				'[hlsjs] ' +
				data.type +
				'/' +
				data.details +
				(data.response ? ' HTTP ' + data.response.code : '');
			const err = new Error(message);
			for (const cb of this.listeners.error) {
				cb({ detail: { message: err.message, stack: err.stack, data } });
			}
			// Sem teto, um 404/403 permanente vira laco apertado de recuperacao
			// e o QoE conta N erros para uma falha unica.
			this.lastRecoveryAttempted = false;
			if (data.type === Ctor.ErrorTypes.NETWORK_ERROR) {
				if (this.networkRecoveries < MAX_RECOVERY_ATTEMPTS) {
					this.networkRecoveries++;
					this.lastRecoveryAttempted = true;
					// Um pauseLoading() do governador de banda e deliberado — a
					// recuperacao nao pode reativar o fetch por cima dele.
					if (!this.loadingPaused) this.hls?.startLoad();
				}
			} else if (data.type === Ctor.ErrorTypes.MEDIA_ERROR) {
				if (this.mediaRecoveries < MAX_RECOVERY_ATTEMPTS) {
					this.mediaRecoveries++;
					this.lastRecoveryAttempted = true;
					this.hls?.recoverMediaError();
				}
			}
		});
		const onWaiting = () => {
			for (const cb of this.listeners.buffering) cb({ buffering: true });
		};
		const onPlaying = () => {
			for (const cb of this.listeners.buffering) cb({ buffering: false });
		};
		const onPlay = () => {
			this.promote();
			this.armDecodeStallCheck();
		};
		const onPause = () => this.clearDecodeStallTimer();
		media.addEventListener('waiting', onWaiting);
		media.addEventListener('playing', onPlaying);
		media.addEventListener('canplay', onPlaying);
		media.addEventListener('play', onPlay);
		media.addEventListener('pause', onPause);
		this.mediaListeners = [
			['waiting', onWaiting],
			['playing', onPlaying],
			['canplay', onPlaying],
			['play', onPlay],
			['pause', onPause]
		];
		window.addEventListener('resize', this.onWindowResize);
		this.hls.attachMedia(media);
	}

	async load(url: string, startTime?: number): Promise<void> {
		if (!this.hls) throw new Error('attach() must run before load()');
		const generation = this.staleGuard.next();
		// Estado por reproducao: um EnginePlayer anexado uma vez pode receber
		// varios load() (troca de video na galeria) — nada disto pode vazar
		// da reproducao anterior para esta.
		this.networkRecoveries = 0;
		this.mediaRecoveries = 0;
		this.bytesFetched = 0;
		this.loadStartedAt = performance.now();
		this.manifestParsedAt = null;
		this.firstFragAt = null;
		this.qualityRamp = [];
		this.levelSwitches = 0;
		this.hevcLevelsRemoved = false;
		this.hevcFallbackReason = null;
		this.ladderHasH264 = false;
		this.startPending = false;
		this.resumeAt = null;
		this.decodeConfirmed = false;
		this.framesAtLoad = this.totalVideoFrames() ?? 0;
		this.sourceUrl = url;
		this.clearDecodeStallTimer();
		const Ctor = await loadHlsEngine();
		if (this.staleGuard.isStale(generation) || !this.hls) {
			throw new Error('[hlsjs] load superseded before it could start');
		}
		if (startTime !== undefined) {
			this.hls.config.startPosition = startTime;
		}
		await new Promise<void>((resolve, reject) => {
			const onParsed = () => {
				cleanup();
				resolve();
			};
			const onError = (_event: unknown, data: ErrorData) => {
				if (!data.fatal) return;
				// O listener permanente de attach() ja tentou recuperar esta mesma
				// emissao (ordem sincrona, FIFO) — so rejeita quando ele desistiu.
				if (this.lastRecoveryAttempted) return;
				cleanup();
				reject(
					new Error('[hlsjs] load failed: ' + data.type + '/' + data.details)
				);
			};
			const cleanup = () => {
				this.hls?.off(Ctor.Events.MANIFEST_PARSED, onParsed);
				this.hls?.off(Ctor.Events.ERROR, onError);
			};
			this.hls!.on(Ctor.Events.MANIFEST_PARSED, onParsed);
			this.hls!.on(Ctor.Events.ERROR, onError);
			this.hls!.loadSource(url);
		});
	}

	addEventListener(event: EngineEvent, cb: (event: any) => void): void {
		this.listeners[event]?.add(cb);
	}

	removeEventListener(event: EngineEvent, cb: (event: any) => void): void {
		this.listeners[event]?.delete(cb);
	}

	getVariantTracks(): EngineVariantTrack[] {
		if (!this.hls) return [];
		const current = this.hls.currentLevel;
		return this.hls.levels.map((level: Level, index: number) => ({
			id: index,
			height: level.height ?? null,
			width: level.width ?? null,
			bandwidth: level.bitrate,
			videoCodec: level.videoCodec,
			active: index === (current === -1 ? this.hls!.loadLevel : current)
		}));
	}

	selectVariantTrack(track: EngineVariantTrack, _clearBuffer?: boolean): void {
		if (!this.hls) return;
		this.hls.currentLevel = track.id;
	}

	/**
	 * A preload that starts playing is the active player from then on: the
	 * buffer cap and the first-fragment priority were only for the neighbor.
	 */
	private promote(): void {
		if (!this.isPreload) return;
		this.isPreload = false;
		if (!this.hls) return;
		const goal = this.pendingConfig.streaming?.bufferingGoal ?? 10;
		Object.assign(this.hls.config, bufferConfig(false, goal));
	}

	/** Pausa/retoma o fetch de segmentos sem desfazer o attach (governador de banda). */
	// startLoad() do hls.js nao e idempotente: reinicia a maquina de estados e
	// aborta o fragmento em voo, entao so chamamos quando o estado muda.
	pauseLoading(): void {
		this.clearDecodeStallTimer();
		if (!this.hls || this.loadingPaused) return;
		this.loadingPaused = true;
		this.hls.stopLoad();
	}

	resumeLoading(): void {
		if (!this.hls || !this.loadingPaused) return;
		this.loadingPaused = false;
		// The ladder is still being gated: prepareStart() starts the load itself.
		if (this.startPending) return;
		// Honors resumeAt when the governor held the fallback's reload.
		this.startLoadOnce();
		this.armDecodeStallCheck();
	}

	async unload(): Promise<void> {
		this.pauseLoading();
	}

	async destroy(): Promise<void> {
		this.staleGuard.next();
		this.clearDecodeStallTimer();
		window.removeEventListener('resize', this.onWindowResize);
		if (this.resizeTimer) clearTimeout(this.resizeTimer);
		this.resizeTimer = null;
		if (this.media) {
			for (const [eventName, handler] of this.mediaListeners) {
				this.media.removeEventListener(eventName, handler);
			}
			this.mediaListeners = [];
		}
		this.hls?.destroy();
		this.hls = null;
		this.media = null;
		this.loadingPaused = false;
		this.startPending = false;
		for (const set of Object.values(this.listeners)) {
			set.clear();
		}
	}
}
